import { esc } from "../escape.js";
import { formatMoney } from "../currency.js";
import { formatInvoiceNumber } from "../invoiceNumber.js";
import { formatPhone } from "../loyalty.js";

// Activity feed - real events derived by model.fetchActivityFeed, cached and
// passed in by the controller. Color associations by tone: good = green
// (sales, time-ins), warn = amber tinted row (low stock), danger = red tinted
// row (out of stock), bad = red icon/amount (money out, voids), info = purple
// (time-outs). `amount` renders as a bold colored figure after the text.

// Relative time label for a feed row. Browser-local, matching the rest of home.
const _relTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Clamp: a timestamp slightly ahead of this device's clock (terminal skew)
  // must age normally instead of sitting on "Just now" until time catches up.
  const diffSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  const clock = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (dayDiff === 0) return clock;
  if (dayDiff === 1) return `Yesterday · ${clock}`;
  if (dayDiff < 7) return `${d.toLocaleDateString([], { weekday: "short" })} · ${clock}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

// One event -> the row's presentation. Sale/void payloads are raw sales rows
// (snake_case); the rest are the small shapes fetchActivityFeed builds.
const _toDisplay = (ev) => {
  const p = ev.payload ?? {};
  switch (ev.type) {
    case "sale": {
      const inv = p.receipt_number ? ` ${formatInvoiceNumber(p.receipt_number, p.sale_date)}` : "";
      return { tone: "good", emoji: "💰", text: `Sale${inv} recorded`, amount: `+${formatMoney(p.total_price)}`, amountTone: "good" };
    }
    case "void": {
      const inv = p.receipt_number ? ` ${formatInvoiceNumber(p.receipt_number, p.sale_date)}` : "";
      return { tone: "bad", emoji: "🚫", text: `Sale${inv} voided`, amount: `-${formatMoney(p.total_price)}`, amountTone: "bad" };
    }
    case "clock_in":
      return { tone: "good", emoji: "👋", text: `${p.staffName} timed in` };
    case "clock_out":
      return { tone: "info", emoji: "👋", text: `${p.staffName} timed out` };
    case "expense":
      return { tone: "bad", emoji: "🧾", text: `Expense · ${p.description}`, amount: `-${formatMoney(p.amount)}`, amountTone: "bad" };
    case "stock_adjust":
      return {
        tone: p.delta > 0 ? "good" : "info",
        emoji: "🗃️",
        text: `${p.itemName} ${p.delta > 0 ? "restocked" : "adjusted"}`,
        amount: `${p.delta > 0 ? "+" : ""}${p.delta} ${p.unit}`,
        amountTone: p.delta > 0 ? "good" : "bad",
      };
    case "customer_enrolled":
      return { tone: "good", emoji: "⭐", text: `${p.name || formatPhone(p.phone)} joined the loyalty program` };
    case "po_received":
      return { tone: "good", emoji: "🚚", text: `PO-${String(p.poNumber ?? 0).padStart(3, "0")} received${p.supplierName ? ` from ${p.supplierName}` : ""} · stock updated` };
    case "low_stock":
      return { tone: "warn", emoji: "⚠️", text: `${p.itemName} is running low · ${p.quantity} ${p.unit} left` };
    case "out_of_stock":
      return { tone: "danger", emoji: "🚨", text: `${p.itemName} is out of stock` };
    default:
      return { tone: "info", emoji: "✨", text: "Update" };
  }
};

const _feedItemsMarkup = (entries) => entries
  .map((ev) => {
    const f = _toDisplay(ev);
    const classes = `home-feed-item home-feed-item--${f.tone}${ev.clickable ? " home-feed-item--click" : ""}`;
    const attrs = ev.clickable ? ` role="button" tabindex="0"` : "";
    return `
    <li class="${classes}" data-evt-id="${esc(ev.id)}"${attrs}>
      <span class="home-feed-icon" aria-hidden="true">${f.emoji}</span>
      <span class="home-feed-text">${esc(f.text)}${f.amount ? ` <strong class="home-feed-amt home-feed-amt--${f.amountTone}">${esc(f.amount)}</strong>` : ""}</span>
      <span class="home-feed-time">${esc(_relTime(ev.ts))}</span>
    </li>`;
  })
  .join("");

// Per-step presentation, mirroring the "Set up your catalogue" banner's
// anatomy: emoji tile + title + one-line subtitle + green pill CTA. The
// owner's direction (2026-07-06): the steps must look like that banner,
// NOT like a checkbox list - so only PENDING steps render (as mini
// banners) and completed ones simply disappear, with the header's
// "N of 6 complete" + progress bar carrying the sense of progress.
const STEP_META = {
  items:     { emoji: "📦",  sub: "Add your items to start taking orders.",        cta: "Add Items"   },
  taxes:     { emoji: "🧾",  sub: "Review the taxes and fees on your receipts.",   cta: "Review"      },
  inventory: { emoji: "🗃️", sub: "Track stock and get low-stock alerts.",         cta: "Explore"     },
  receipt:   { emoji: "🖨️", sub: "Choose what shows on your receipts.",           cta: "Customize"   },
  team:      { emoji: "👥",  sub: "Invite staff with their own logins and roles.", cta: "Set Up"      },
  sale:      { emoji: "🛒",  sub: "Ring up your first order at the register.",     cta: "New Order"   },
};

// Home "Getting Started" card (#homeChecklistCard). The controller computes
// the steps from live state and calls render(); every pending step's CTA
// deep-links to the panel where the step is done. When the last step
// completes while the card is on screen, the controller swaps in a short
// celebration (renderCelebrate) and then hides the card for good.
class GettingStartedView {
  _card = document.getElementById("homeChecklistCard");
  _list = document.getElementById("homeChecklistList");
  _sub  = document.getElementById("homeChecklistSub");
  _fill = document.getElementById("homeChecklistFill");

  render(steps) {
    if (!this._card) return;
    // Reset any activity-mode chrome (defensive - the checklist never returns
    // once the feed takes over, but render order shouldn't be able to wedge it).
    document.getElementById("homeChecklistTitle").textContent = "Getting Started";
    document.getElementById("homeChecklistProgress")?.classList.remove("hidden");
    document.getElementById("homeChecklistDismiss")?.classList.remove("hidden");
    document.getElementById("homeActivityViewAll")?.classList.add("hidden");
    document.getElementById("homeChecklistFoot")?.classList.remove("hidden");
    this._list.classList.remove("home-feed-list");
    const done = steps.filter((s) => s.done).length;
    if (this._sub) {
      this._sub.textContent = done === 0
        ? "Set up your store step by step"
        : `${done} of ${steps.length} complete`;
    }
    if (this._fill) this._fill.style.width = `${(done / steps.length) * 100}%`;
    this._list.innerHTML = steps
      .filter((s) => !s.done)
      .map((s) => {
        const meta = STEP_META[s.id] ?? { emoji: "✨", sub: "", cta: "Open" };
        return `
        <li>
          <div class="home-checklist-task">
            <span class="home-checklist-task-icon" aria-hidden="true">${meta.emoji}</span>
            <div class="home-checklist-task-text">
              <div class="home-checklist-task-title">${esc(s.label)}</div>
              <div class="home-checklist-task-sub">${esc(meta.sub)}</div>
            </div>
            <button class="btn primary home-checklist-cta" data-step="${s.id}" type="button">${esc(meta.cta)}</button>
          </div>
        </li>`;
      })
      .join("");
    this._card.classList.remove("hidden");
  }

  renderCelebrate(total) {
    if (!this._card) return;
    if (this._sub) this._sub.textContent = `All ${total} steps complete`;
    if (this._fill) this._fill.style.width = "100%";
    this._list.innerHTML = `
      <li class="home-checklist-done">
        <span class="home-checklist-done-emoji" aria-hidden="true">🎉</span>
        <p class="home-checklist-done-title">You're all set!</p>
        <p class="home-checklist-done-sub">Your store is ready for business. This card will make way for your activity feed soon.</p>
      </li>`;
    this._card.classList.remove("hidden");
  }

  hide() {
    this._card?.classList.add("hidden");
  }

  // ── Activity feed ────────────────────────────────────────────────────────────
  // Takes over the card once the checklist is completed or dismissed. Header
  // swaps to "Activity", the progress bar and the x give way to "View all"
  // (which opens the expanded modal). Entries come from the controller's
  // cached model.fetchActivityFeed result; { loaded } distinguishes "still
  // fetching" (quiet card) from "genuinely no activity" (empty state).

  renderActivityFeed(entries, { loaded = true } = {}) {
    if (!this._card) return;
    document.getElementById("homeChecklistTitle").textContent = "Activity";
    if (this._sub) this._sub.textContent = "What's been happening in your store";
    document.getElementById("homeChecklistProgress")?.classList.add("hidden");
    document.getElementById("homeChecklistDismiss")?.classList.add("hidden");
    document.getElementById("homeActivityViewAll")?.classList.remove("hidden");
    this._list.classList.add("home-feed-list");
    if (!loaded) {
      this._list.innerHTML = "";
    } else if (!entries.length) {
      this._list.innerHTML = `<li class="home-feed-empty">No activity yet - sales, clock-ins, expenses and stock changes will show up here.</li>`;
    } else {
      // All 15 cached entries: the card is a fixed-height bento fill (its
      // bottom edge lines up with the Active Queue panel), so the list is
      // always full at any viewport and scrolls for the rest.
      this._list.innerHTML = _feedItemsMarkup(entries);
    }
    // The checklist footnote has no feed equivalent - hide it so its padding
    // doesn't eat row space (render() unhides it for the checklist).
    document.getElementById("homeChecklistFoot")?.classList.add("hidden");
    this._card.classList.remove("hidden");
  }

  showActivityModal(entries) {
    const modal = document.getElementById("activityLogModal");
    if (!modal) return;
    this.renderActivityModalList(entries);
    modal.classList.remove("hidden");
  }

  // Split out so the controller can live-update the list while the modal is open.
  renderActivityModalList(entries) {
    const list = document.getElementById("activityModalList");
    if (!list) return;
    list.innerHTML = entries.length
      ? _feedItemsMarkup(entries)
      : `<li class="home-feed-empty">No activity yet - sales, clock-ins, expenses and stock changes will show up here.</li>`;
  }

  isActivityModalOpen() {
    return !document.getElementById("activityLogModal")?.classList.contains("hidden");
  }

  closeActivityModal() {
    document.getElementById("activityLogModal")?.classList.add("hidden");
  }

  _addHandlerViewAll(handler) {
    document.getElementById("homeActivityViewAll")?.addEventListener("click", () => handler());
    document.getElementById("activityModalCloseBtn")?.addEventListener("click", () => this.closeActivityModal());
  }

  _addHandlerActivityClick(handler) {
    const dispatch = (e) => {
      const row = e.target.closest(".home-feed-item--click");
      if (!row) return;
      if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
      if (e.type === "keydown") e.preventDefault();
      handler(row.dataset.evtId);
    };
    [this._list, document.getElementById("activityModalList")].forEach((el) => {
      el?.addEventListener("click", dispatch);
      el?.addEventListener("keydown", dispatch);
    });
  }

  _addHandlerStep(handler) {
    this._list?.addEventListener("click", (e) => {
      const btn = e.target.closest(".home-checklist-cta");
      if (!btn) return;
      handler(btn.dataset.step);
    });
  }

  _addHandlerDismiss(handler) {
    document.getElementById("homeChecklistDismiss")?.addEventListener("click", () => handler());
  }
}

export default new GettingStartedView();
